import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { t } from 'i18next';
import {
  MediaProjectInstanceResponse,
  CallToActions,
  CustomTheme,
} from '../types';
import Player from 'video.js/dist/types/player';
import { Button, Flex } from 'antd';
import { OverlayAction } from './CallToAction';
import { CallToActionList } from './CallToActions';
import styled, { useTheme } from 'styled-components';
import { Logo } from './Logo';
import { useMediaQuery } from 'react-responsive';
import { SuggestionsOverlay } from './Chat/SuggestionsOverlay';
import { MobileChatFloat } from './Chat/MobileChatFloat';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { LanguageSelector } from './LanguageSelector';
import { shouldShowLanguageSelector } from '../helpers';

const MobileOverlay = ({
  publication,
  documentIds,
  organizationId,
  callToActions,
  playerRef,
  setShowMobilePlayButton,
  openChat,
  changeLanguage,
  locale,
  hasChat,
}: {
  publication: MediaProjectInstanceResponse;
  documentIds: string[];
  organizationId: string;
  playerRef: React.RefObject<Player>;
  callToActions: CallToActions | undefined;
  setShowMobilePlayButton: Dispatch<SetStateAction<boolean>>;
  openChat: boolean;
  changeLanguage: (language: string) => void;
  locale: string;
  hasChat: boolean;
}) => {
  const [show, setShow] = useState(false);
  const theme = useTheme() as unknown as CustomTheme;
  const isLandscape = useMediaQuery({
    query: '(max-width: 1000px) and (orientation: landscape)',
  });

  const [suggestions, setSuggestions] = useState<string[]>();
  const [firstQuery, setFirstQuery] = useState<string>();
  const [chatExpanded, setChatExpanded] = useState(false);
  const showLanguageSelector = shouldShowLanguageSelector(
    publication?.project_id,
  );

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const onEnded = () => {
      setShowMobilePlayButton(true);
    };

    const checkPlayerReady = () => {
      const player = playerRef.current;
      if (
        player &&
        !player.isDisposed() &&
        typeof player.ready === 'function'
      ) {
        player.ready(() => {
          if (cancelled || player.isDisposed()) return;
          player.on('ended', onEnded);
        });
      } else if (!cancelled) {
        timerId = setTimeout(checkPlayerReady, 100);
      }
    };

    checkPlayerReady();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.off('ended', onEnded);
      }
    };
  }, [playerRef, setShowMobilePlayButton]);

  return (
    <>
      <MobileHeader className="mobile-header">
        {callToActions?.actions?.length || hasChat ? (
          <Button
            style={{
              color: '#555',
              border: 0,
              boxShadow: 'none',
            }}
            icon={
              show ? (
                <CloseOutlined style={{ fontSize: '20px' }} />
              ) : (
                <MenuOutlined style={{ fontSize: '20px' }} />
              )
            }
            onClick={() => setShow((prev) => !prev)}
          />
        ) : (
          <span></span>
        )}
        <div style={{ width: '120px' }}>
          <Logo
            showLogo={true}
            margin={0}
            projectId={publication?.project_id}
            customUrl={publication?.assets?.Logo?.path}
          />
        </div>
        <Flex gap={10} align="center">
          {showLanguageSelector && (
            <LanguageSelector changeLanguage={changeLanguage} />
          )}
        </Flex>
      </MobileHeader>
      {hasChat && (
        <SuggestionsOverlay
          suggestions={suggestions}
          setLayout={() => setChatExpanded(true)}
          setNextQuery={setFirstQuery}
          playerRef={playerRef}
        />
      )}
      {hasChat && (
        <MobileChatFloat
          publication={publication}
          documentIds={documentIds}
          organizationId={organizationId}
          openChat={openChat}
          firstQuery={firstQuery}
          suggestions={suggestions}
          setSuggestions={setSuggestions}
          expanded={chatExpanded}
          onExpandedChange={setChatExpanded}
          isLegacy
        />
      )}
      {show && (
        <StyledCTAMobileContainer
          onClick={() => setShow(false)}
          style={
            isLandscape
              ? {
                  width: '50%',
                  height: 'fit-content',
                  left: '50%',
                  transform: 'translate(-50%)',
                  zIndex: 25,
                }
              : { zIndex: 25 }
          }
        >
          <Flex vertical onClick={(e) => e.stopPropagation()} gap={30}>
            {callToActions && (
              <CallToActionList
                callToActions={callToActions}
                publication={publication}
                locale={locale}
              />
            )}
            {hasChat && (
              <OverlayAction
                color={theme.colors.ctaTextColor}
                index={2}
                onClick={() => {
                  setChatExpanded(true);
                  setShow(false);
                }}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient
                        id="gradient"
                        x1="0%"
                        x2="100%"
                        y1="0%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="white" />
                        <stop offset="100%" stopColor="yellow" />
                      </linearGradient>
                    </defs>
                    <path
                      fill="url(#gradient)"
                      d="M8.03339 3.65784C8.37932 2.78072 9.62068 2.78072 9.96661 3.65785L11.0386 6.37599C11.1442 6.64378 11.3562 6.85576 11.624 6.96137L14.3422 8.03339C15.2193 8.37932 15.2193 9.62068 14.3422 9.96661L11.624 11.0386C11.3562 11.1442 11.1442 11.3562 11.0386 11.624L9.96661 14.3422C9.62067 15.2193 8.37932 15.2193 8.03339 14.3422L6.96137 11.624C6.85575 11.3562 6.64378 11.1442 6.37599 11.0386L3.65784 9.96661C2.78072 9.62067 2.78072 8.37932 3.65785 8.03339L6.37599 6.96137C6.64378 6.85575 6.85576 6.64378 6.96137 6.37599L8.03339 3.65784Z"
                    />
                    <path
                      fill="url(#gradient)"
                      d="M16.4885 13.3481C16.6715 12.884 17.3285 12.884 17.5115 13.3481L18.3121 15.3781C18.368 15.5198 18.4802 15.632 18.6219 15.6879L20.6519 16.4885C21.116 16.6715 21.116 17.3285 20.6519 17.5115L18.6219 18.3121C18.4802 18.368 18.368 18.4802 18.3121 18.6219L17.5115 20.6519C17.3285 21.116 16.6715 21.116 16.4885 20.6519L15.6879 18.6219C15.632 18.4802 15.5198 18.368 15.3781 18.3121L13.3481 17.5115C12.884 17.3285 12.884 16.6715 13.3481 16.4885L15.3781 15.6879C15.5198 15.632 15.632 15.5198 15.6879 15.3781L16.4885 13.3481Z"
                    />
                  </svg>
                }
              >
                {t('askQuestionsAlt')}
              </OverlayAction>
            )}
          </Flex>
        </StyledCTAMobileContainer>
      )}
    </>
  );
};

export default MobileOverlay;
const StyledCTAMobileContainer = styled.div`
  position: absolute;
  top: 0;
  box-sizing: border-box;
  z-index: 4;
  left: 0;
  padding: 20px;
  padding-top: 100px;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);

  .wrapper {
    height: fit-content !important;
  }
`;

const MobileHeader = styled.div`
  z-index: 1000;
  position: fixed;
  top: 0;
  width: 100%;
  height: var(--theme-mobile-header-height);
  padding: 10px;
  box-sizing: border-box;
  background-color: white;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 1;
  transition: opacity 0.3s ease-out;
`;
